/** @prettier */

import {
  Grid,
  Box,
  Container,
  Skeleton,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Switch,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  StyledEngineProvider,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import {useEffect, useState, useRef, createRef} from 'react';
import {useParams, useLocation, useLoaderData} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {isUndefined} from 'lodash';
import {useTranslation} from 'react-i18next';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {format} from 'date-fns';
import {Storage} from 'aws-amplify';

//MTH component import
import Footer from '../MainFooter';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsLayoutImage,
  getCompositionsGroups,
  getSettingUser,
  patchSettingUser,
  patchCompositionsGroups,
  getStatesSitesModbus,
  getStatesSitesBySiteID,
  getCompositionsSitesBySiteID,
  getStatesSitesRt,
  getWeatherCurrent,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import * as mySiteLib from './mySiteLibrary';
import {limit, file, iconBlack, iconWhite, iconRed} from '../../define';
import getLogo from '../../functions/getLogo';
import {
  invertComma,
  numberWithPeriod,
  roundOff,
} from '../../functions/convertString';
import {getLanguageForAPIQuery} from '../../functions/weatherAPI';
import {addTranslation} from '../../functions/addTranslation';

type displaySettingType = {
  /** 自動ページ切り替え */
  autoSwitching: boolean;
  /** ページ切り替え時間間隔 */
  timeFrame: apiIF.res.getSettingUser['modbus-display']['switching-time-frame'];
  /** シングルビュー ツールチップの項目数 */
  singleFloorPlan: apiIF.res.getSettingUser['modbus-display']['display-item']['single-floor-plan'];
  /** マルチビュー ツールチップの項目数 */
  multiFloorPlan: apiIF.res.getSettingUser['modbus-display']['display-item']['multi-floor-plan'];
};
type GroupInfoType = {
  /** グループID */
  groupID: string;
  /** グループ名 */
  groupName: string;
  /** マルチビューの表示設定 */
  stateDisplay: 'active' | 'inactive';
  unitInformations: {
    /** ユニットID */
    unitID: string;
    /** ユニット名 */
    unitName: string;
    /** ユニットアイコンのx座標 */
    xCoordinate: string;
    /** ユニットアイコンのy座標 */
    yCoordinate: string;
  }[];
  /** レイアウト画像 */
  layout: string | null;
};
type iuStateType = {
  /** 室内機ID */
  iuID: string;
  /** 運転停止状態 */
  runStop: 'run' | 'stop';
  /** 設定温度 */
  presetTemp: string;
  /** 庫内温度 */
  insideTemp: string;
  /** 温度差の警戒レベル */
  differenceLevel: 'safety' | 'carefully(low)' | 'carefully(high)' | 'warning';
  /** 接続状態 Hyozan限定*/
  connectionStatus?: 'notConnected' | 'connected' | '';
};
type LoaderReturnType = {
  /** M-ACCESSのロゴデータ（取得できない場合は「default」が入る） */
  maccessLogo: string;
  /** MITSUBISHIのロゴデータ */
  mhiLogo: string;
  /** サイトの名前 */
  siteName: apiIF.res.getCompositionsSitesBySiteID['site-name'];
  /** タイムゾーン */
  timeZone: apiIF.res.getCompositionsSitesBySiteID['timezone'];
  /** 天気 */
  weather: apiIF.res.getWeatherCurrent;
  /** 機種形式 */
  unitType: 'Hyozan' | 'Modbus';
  /** 表示設定 */
  displaySetting: displaySettingType;
  /** フッターテキスト */
  footerText: apiIF.res.getSettingUser['footer-text'];
  /** 言語設定 */
  language: apiIF.res.getSettingUser['language'];
};

async function GroupDetailPopOutLoader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.unitType === undefined) {
    return undefined;
  }
  if (params.unitType !== 'Hyozan' && params.unitType !== 'Modbus') {
    return undefined;
  }

  try {
    let promises = [];
    //ロゴを取得
    let maccessLogo: LoaderReturnType['maccessLogo'] = '';
    let mhiLogo: LoaderReturnType['mhiLogo'] = '';
    promises.push(
      getLogo(
        logo => {
          maccessLogo = logo;
        },
        logo => {
          mhiLogo = logo;
        }
      )
    );
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //ユーザ情報を取得
    promises.push(getSettingUser());
    const response = await Promise.all(promises);

    const siteInfo = response[1] as apiIF.res.getCompositionsSitesBySiteID;
    const userSettingInfo = response[2] as apiIF.res.getSettingUser;
    //weatherAPI用の言語を取得
    const weatherData = await getWeatherCurrent({
      location: siteInfo.location,
      language: getLanguageForAPIQuery(userSettingInfo.language),
    });

    return {
      maccessLogo: maccessLogo,
      mhiLogo: mhiLogo,
      siteName: siteInfo['site-name'],
      timeZone: siteInfo['timezone'],
      weather: weatherData,
      unitType: params.unitType,
      //prettier-ignore
      displaySetting: {
        autoSwitching: userSettingInfo['modbus-display']['auto-switching'] === 'active',
        timeFrame: userSettingInfo['modbus-display']['switching-time-frame'],
        singleFloorPlan: userSettingInfo['modbus-display']['display-item']['single-floor-plan'],
        multiFloorPlan: userSettingInfo['modbus-display']['display-item']['single-floor-plan'],
      },
      footerText: userSettingInfo['footer-text'],
      language: userSettingInfo['language'],
    };
  } catch {
    return undefined;
  }
}

/**
 * HCCV(Q)グループ詳細（ポップアウト用）
 * @returns JSX
 */
const GroupDetailPopOut = () => {
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  const trans = useTranslation().t; //翻訳関数

  if (loaderData === undefined) {
    return <></>;
  }
  return (
    <>
      <header>
        <Grid
          container
          columnSpacing={3}
          justifyContent="space-between"
          alignItems="center">
          {/* M-ACCESSのロゴ */}
          <Grid item xs="auto" className="logoBox">
            {loaderData.maccessLogo === 'default' ? (
              <img
                className="logoBox__icon--m-accessLogo"
                src={file.MACCESS_LOGO_PATH}
                alt={`${trans(
                  `HCCVQGroupDetail.image.alternativeText.mAccessLogo`
                )}`}
                id="mv-mAccessLogo"
              />
            ) : loaderData.maccessLogo === '' ? (
              <Box className="logoBox__icon--m-accessLogo"></Box>
            ) : (
              <img
                className="logoBox__icon--m-accessLogo"
                src={loaderData.maccessLogo}
                alt={`${trans(
                  `HCCVQGroupDetail.image.alternativeText.changedMAccessLogo`
                )}`}
              />
            )}
          </Grid>
          {/* サイトの名前 */}
          <Grid item xs className="siteName">
            {loaderData.siteName}
          </Grid>
          {/* 天気予報 */}
          <Grid item xs="auto" className="logoBox">
            <img
              className="logoBox__icon--weather"
              src={loaderData.weather.current.condition.icon.replace(
                '//cdn.weatherapi.com',
                `${process.env.PUBLIC_URL}/img/mysite/icon`
              )}
              alt={`${trans(
                `HCCVQGroupDetail.image.alternativeText.currentWeatherIcon`
              )}`}
              title={loaderData.weather.current.condition.text}
            />
          </Grid>
          <Grid item xs={1} className="temperature">
            <Box>
              {loaderData.weather.current.temp_c}
              {trans('HCCVQGroupDetail.unit.temperatureUnit')}
            </Box>
          </Grid>
          {/* MHIのロゴ */}
          <Grid item xs="auto" className="logoBox">
            {loaderData.mhiLogo === 'default' ? (
              <img
                className="logoBox__icon--mhiLogo"
                src={file.MHI_LOGO_PATH}
                alt={`${trans(
                  `HCCVQGroupDetail.image.alternativeText.mhiLogo`
                )}`}
                id="mv-mhiLogo"
              />
            ) : loaderData.mhiLogo === '' ? (
              <Box className="logoBox__icon--mhiLogo"></Box>
            ) : (
              <img
                className="logoBox__icon--mhiLogo"
                src={loaderData.mhiLogo}
                alt={`${trans(
                  `HCCVQGroupDetail.image.alternativeText.changedMhiLogo`
                )}`}
              />
            )}
          </Grid>
        </Grid>
      </header>
      <main>
        <GroupDetail
          isPopOut={true}
          unitType={loaderData.unitType}
          timezone={loaderData.timeZone}
          displaySetting={loaderData['displaySetting']}
          language={loaderData.language}
        />
      </main>
      <Footer footerText={loaderData.footerText} />
    </>
  );
};
/**
 * HCCV(Q)グループ詳細
 * @param props props
 * @returns JSX.Element
 */
const GroupDetail = (props: {
  /** 別タブ表示 */
  isPopOut?: boolean;
  /** 選択された室内機 */
  selectedItem?: string;
  /** 選択された室内機の更新関数 */
  setSelectedItem?: React.Dispatch<React.SetStateAction<string>>;
  /** 機種型式 */
  unitType: 'Hyozan' | 'Modbus';
  /** 登録されているサイトのタイムゾーン */
  timezone: string;
  /** 表示設定 */
  displaySetting: displaySettingType;
  /** 言語設定 */
  language: string;
}) => {
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID, targetID: groupID} = useParams();
  //翻訳関数、i18n
  const [trans, internationalization] = useTranslation();

  //ダイアログ表示フラグ
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  //HCCVQグループ一覧
  const [hccvqGroupList, setHCCVQGroupList] = useState<GroupInfoType[]>([]);
  //prettier-ignore
  //表示形式
  const [displayMode, setDisplayMode] = useState<'player' | 'multiView'>('player');
  //表示設定
  const [displaySetting, setDisplaySetting] = useState(props.displaySetting);
  //HCCVQ全体の消費電力量
  const [hccvqPower, setHCCVQPower] = useState('---');
  //シングルビューアイコン
  const [playerIcon, setPlayerIcon] = useState(iconRed.PLAYER.src);
  //マルチビューアイコン
  const [multiViewIcon, setMultiView] = useState(iconBlack.MULTI_VIEW.src);
  //定期更新用時刻
  const [now, setNow] = useState(new Date());

  //シングルビュー
  //prettier-ignore
  //表示しているグループ
  const [singleDisplayGroup, setSingleDisplayGroup] = useState<GroupInfoType | null>(null);

  //マルチビュー
  //prettier-ignore
  //表示できるグループ
  const [multiDisplayGroupList, setMultiDisplayGroupList] = useState<GroupInfoType[]>([]);
  //prettier-ignore
  //表示しているグループ
  //要素数が2以下の場合：[0]左、[1]右   要素数が3以上の場合：[0]左上、[1]右上、[2]左下、[3]右下
  const [multiDisplayGroup, setMultiDisplayGroup] = useState<GroupInfoType[]>([]);

  //グループの情報を取得（表示形式を変更するたびに実行）
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID) || isUndefined(groupID)) return;

    (async () => {
      const allGroupInfo = await getCompositionsGroups(siteID, {
        'fields': 'group-name,unit-system,state-display,unit-informations',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      });

      //Hyozan or Modbusのグループのみ抽出
      let unitSystem: 'RT' | 'Modbus';
      if (props.unitType === 'Hyozan') {
        unitSystem = 'RT';
      } else if (props.unitType === 'Modbus') {
        unitSystem = 'Modbus';
      } else {
        return;
      }
      const hccvqGroup: GroupInfoType[] = allGroupInfo['group-informations']
        .filter(groupInfo => groupInfo['unit-system'] === unitSystem)
        .map(groupInfo => {
          //prettier-ignore
          return {
            groupID          : groupInfo['group-id'],
            groupName        : groupInfo['group-name'] === undefined ? '' : groupInfo['group-name'],
            stateDisplay     : groupInfo['state-display'] === 'active' ? 'active' : 'inactive',
            layout           : null,
            unitInformations : groupInfo['unit-informations'] === undefined
                ? []
                : groupInfo['unit-informations'].map(unitInfo => ({
                    unitID: unitInfo['unit-id'],
                    unitName: unitInfo['unit-name'],
                    xCoordinate: unitInfo['x-coordinate'],
                    yCoordinate: unitInfo['y-coordinate'],
                  })),
          };
        });
      //レイアウト画像取得
      const promises = hccvqGroup.map(groupInfo => {
        return (async () => {
          const response = await getCompositionsLayoutImage(
            siteID,
            groupInfo.groupID
          );
          return {
            groupID: groupInfo.groupID,
            layoutImage: response['layout-image'],
          };
        })();
      });
      const groupLayout = await Promise.all(promises);
      //レイアウト画像格納
      const newHCCVQGroup = hccvqGroup.map(groupInfo => {
        const layout = groupLayout.find(info => {
          return info.groupID === groupInfo.groupID;
        });
        if (layout === undefined) {
          return groupInfo;
        }
        let newInfo = JSON.parse(JSON.stringify(groupInfo)) as GroupInfoType;
        newInfo.layout = layout.layoutImage;
        return newInfo;
      });
      setHCCVQGroupList(newHCCVQGroup);

      //シングルビュー
      const temp = newHCCVQGroup.find(info => info.groupID === groupID);
      if (temp !== undefined) {
        setSingleDisplayGroup(temp);
      }
      //マルチビュー
      setMultiDisplayGroupList(
        newHCCVQGroup.filter(info => info.stateDisplay === 'active')
      );
    })();
  }, [siteID, groupID, displayMode, props.unitType]);

  //Hyozan or Modbus全体の消費電力量を取得
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID)) return;

    const nowDate = new Date();
    //prettier-ignore
    //開始を午前0時とする
    const start = format(utcToZonedTime(nowDate, props.timezone), 'yyyy-MM-dd 00:00:00' );
    const afterDate = zonedTimeToUtc(start, props.timezone);

    const query: apiIF.reqParameters.getStatesSitesBySiteID = {
      'fields': 'power-consumption',
      'after-time': afterDate.toISOString(),
      'before-time': nowDate.toISOString(), //終了を現在時刻とする
      'every-minute': 1440,
    };
    getStatesSitesBySiteID(siteID, query).then(response => {
      /** 最新の消費電力量（配列の先頭が最新データ） */
      let latestPower = undefined;
      if (props.unitType === 'Hyozan') {
        latestPower = response.states[0]['hyozan']['power-consumption'];
      } else if (props.unitType === 'Modbus') {
        latestPower = response.states[0]['hccvq']['power-consumption'];
      }

      if (latestPower !== undefined && latestPower['cooling-mode'] !== '') {
        setHCCVQPower(String(mySiteLib.convNumKw(latestPower['cooling-mode'])));
      }
    });
  }, [siteID, props.unitType, props.timezone]);

  //定期更新
  useEffect(() => {
    const interval = setInterval(() => {
      //パスパラメータを取得できない場合
      if (isUndefined(siteID)) return;

      //prettier-ignore
      //開始を午前0時とする
      const start = format(utcToZonedTime(now, props.timezone), 'yyyy-MM-dd 00:00:00' );
      const afterDate = zonedTimeToUtc(start, props.timezone);

      const query: apiIF.reqParameters.getStatesSitesBySiteID = {
        'fields': 'power-consumption',
        'after-time': afterDate.toISOString(),
        'before-time': now.toISOString(), //終了を現在時刻とする
        'every-minute': 1440,
      };
      getStatesSitesBySiteID(siteID, query).then(response => {
        /** 最新の消費電力量（配列の先頭が最新データ） */
        let latestPower = undefined;
        if (props.unitType === 'Hyozan') {
          latestPower = response.states[0]['hyozan']['power-consumption'];
        } else if (props.unitType === 'Modbus') {
          latestPower = response.states[0]['hccvq']['power-consumption'];
        }

        if (latestPower !== undefined && latestPower['cooling-mode'] !== '') {
          setHCCVQPower(
            String(mySiteLib.convNumKw(latestPower['cooling-mode']))
          );
        }
      });

      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  //マルチビューの表示しているグループを更新
  useEffect(() => {
    setMultiDisplayGroup(multiDisplayGroupList);
  }, [multiDisplayGroupList]);

  //グループ自動切換え
  useEffect(() => {
    //シングルビュー かつ レイアウト自動切換えが有効の場合のみ実行
    if (
      displayMode !== 'player' ||
      displaySetting.autoSwitching === false ||
      hccvqGroupList.length === 0
    ) {
      return;
    }

    const interval = setInterval(() => {
      if (singleDisplayGroup === null) {
        setSingleDisplayGroup(hccvqGroupList[0]);
      } else {
        switchUpGroup(
          singleDisplayGroup.groupID,
          setSingleDisplayGroup,
          hccvqGroupList
        );
      }
    }, Number(displaySetting.timeFrame) * 1000);
    return () => clearInterval(interval);
  }, [displayMode, displaySetting, singleDisplayGroup, hccvqGroupList]);

  //言語設定適用処理
  useEffect(() => {
    //ポップアウト時のみ実施
    if (props.isPopOut === undefined || props.isPopOut === false) {
      return;
    }

    //言語設定がcustom以外の場合
    if (props.language !== 'custom') {
      internationalization.changeLanguage(props.language);
    } else {
      //ユーザ言語の読み込み処理
      Storage.configure({customPrefix: {private: file.LANGUAGE_S3_KEY_PREFIX}});
      //S3内のファイル一覧を取得
      Storage.list('', {level: 'private'})
        .then(result => {
          //言語ファイルの有無を検索
          const hasUserLang = result.results.some(info => {
            return info['key'] === file.CUSTOM_TRANSLATION_NAME;
          });
          //言語ファイルがある場合、S3内の言語ファイルを取得
          if (hasUserLang) {
            Storage.get(file.CUSTOM_TRANSLATION_NAME, {
              level: 'private',
            })
              .then(url => {
                //ファイルを読み込む
                return fetch(url);
              })
              .then(response => {
                return response.text();
              })
              .then(data => {
                addTranslation(data); //言語データを翻訳リソースに追加する
                internationalization.changeLanguage(props.language); //追加した翻訳を適用する
              })
              .catch(err => {});
          }
        })
        .catch(err => {});
    }
  }, [props.isPopOut, props.language, internationalization]);

  /**
   * 表示形式変更時
   * @param event
   * @param mode
   */
  const handleChangeDisplayMode = (
    event: React.MouseEvent<HTMLElement>,
    mode: 'player' | 'multiView' | null
  ) => {
    if (mode !== null) {
      setDisplayMode(mode);
      if (mode === 'player') {
        setPlayerIcon(iconRed.PLAYER.src);
        setMultiView(iconBlack.MULTI_VIEW.src);
      } else if (mode === 'multiView') {
        setPlayerIcon(iconBlack.PLAYER.src);
        setMultiView(iconRed.MULTI_VIEW.src);
      }
    }
  };

  return (
    <Container fixed className="modbusGroupLayoutArea">
      <Stack>
        {/** タイトル */}
        <Box>
          <Grid
            container
            columnSpacing={2}
            alignItems="center"
            className="modbusGroupLayoutArea__title">
            <Grid item xs={12} xl="auto">
              {trans('HCCVQGroupDetail.title.otherEquipment')}
            </Grid>
            {/** HCCV(Q)全体の消費電力量 */}
            <Grid item xs={12} lg>
              <Box className="modbusGroupLayoutArea__title__power">
                {trans('HCCVQGroupDetail.title.totalPower')}
                <Box
                  component="span"
                  className="modbusGroupLayoutArea__title__power__num">
                  {
                    //prettier-ignore
                    `${hccvqPower === '---' ? hccvqPower : numberWithPeriod(roundOff(hccvqPower))}${trans('HCCVQGroupDetail.unit.powerUnit')}`
                  }
                </Box>
              </Box>
            </Grid>
            {/** 表示形式 */}
            <Grid item xs lg="auto">
              <ToggleButtonGroup
                color="primary"
                exclusive
                onChange={handleChangeDisplayMode}
                value={displayMode}>
                <ToggleButton value="player">
                  <Grid
                    container
                    columnSpacing={1}
                    alignItems="center"
                    className="modbusGroupLayoutArea__title__toggleButton">
                    <Grid
                      item
                      xs="auto"
                      className="modbusGroupLayoutArea__title__toggleButton__iconContainer">
                      <img
                        src={playerIcon}
                        alt={`${trans(
                          `HCCVQGroupDetail.image.alternativeText.${iconBlack.PLAYER.alt}`
                        )}`}
                        className="modbusGroupLayoutArea__title__toggleButton__iconContainer__icon"
                      />
                    </Grid>
                    <Grid item xs>
                      {trans('HCCVQGroupDetail.buttonTxt.player')}
                    </Grid>
                  </Grid>
                </ToggleButton>
                <ToggleButton value="multiView">
                  <Grid
                    container
                    columnSpacing={1}
                    alignItems="center"
                    className="modbusGroupLayoutArea__title__toggleButton">
                    <Grid
                      item
                      xs="auto"
                      className="modbusGroupLayoutArea__title__toggleButton__iconContainer">
                      <img
                        src={multiViewIcon}
                        alt={`${trans(
                          `HCCVQGroupDetail.image.alternativeText.${iconBlack.MULTI_VIEW.alt}`
                        )}`}
                        className="modbusGroupLayoutArea__title__toggleButton__iconContainer__icon"
                      />
                    </Grid>
                    <Grid item xs>
                      {trans('HCCVQGroupDetail.buttonTxt.multiView')}
                    </Grid>
                  </Grid>
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {/** 設定ボタン */}
            <Grid item xs="auto">
              {siteID !== undefined && (
                <>
                  <IconButton onClick={() => setIsOpenDialog(true)}>
                    <img
                      title={`${trans(
                        `HCCVQGroupDetail.image.title.${iconBlack.SETTINGS.title}`
                      )}`}
                      src={iconBlack.SETTINGS.src}
                      alt={`${trans(
                        `HCCVQGroupDetail.image.alternativeText.${iconBlack.SETTINGS.alt}`
                      )}`}
                      className="modbusGroupLayoutArea__title__icon"
                    />
                  </IconButton>
                  {/** 表示設定ダイアログ */}
                  <PlayerSettingDialog
                    isPopOut={props.isPopOut === true}
                    isOpenDialog={isOpenDialog}
                    setIsOpenDialog={setIsOpenDialog}
                    siteID={siteID}
                    groupList={hccvqGroupList}
                    displayMode={displayMode}
                    displaySetting={displaySetting}
                    setDisplaySetting={setDisplaySetting}
                    multiDisplayGroupList={multiDisplayGroupList}
                    setMultiDisplayGroupList={setMultiDisplayGroupList}
                  />
                </>
              )}
            </Grid>
            {/* ポップアップボタン */}
            {props.isPopOut !== true && (
              <Grid
                item
                xs="auto"
                className="modbusGroupLayoutArea__title__iconButton">
                <IconButton
                  href={`/pop-out/my-site/${siteID}/${groupID}${location.search}/${props.unitType}`}
                  target="_blank">
                  <img
                    title={`${trans(
                      `HCCVQGroupDetail.image.title.${iconBlack.POP_OUT.title}`
                    )}`}
                    src={iconBlack.POP_OUT.src}
                    alt={`${trans(
                      `HCCVQGroupDetail.image.alternativeText.${iconBlack.POP_OUT.alt}`
                    )}`}
                    className="modbusGroupLayoutArea__title__icon"
                  />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Box>
        {/* 状態値 */}
        {isUndefined(siteID) ? (
          <></>
        ) : displayMode === 'player' && singleDisplayGroup !== null ? (
          <SingleView
            siteID={siteID}
            groupList={hccvqGroupList}
            displayGroup={singleDisplayGroup}
            setDisplayGroup={setSingleDisplayGroup}
            floorPlan={displaySetting.singleFloorPlan}
            selectedItem={
              props.selectedItem === undefined ? '' : props.selectedItem
            }
            setSelectedItem={props.setSelectedItem}
            isOpenDialog={isOpenDialog}
            unitType={props.unitType}
          />
        ) : displayMode === 'multiView' && multiDisplayGroupList.length > 0 ? (
          <MultiView
            siteID={siteID}
            displayGroupList={multiDisplayGroupList}
            displayGroup={multiDisplayGroup}
            setDisplayGroup={setMultiDisplayGroup}
            floorPlan={displaySetting.multiFloorPlan}
            selectedItem={
              props.selectedItem === undefined ? '' : props.selectedItem
            }
            setSelectedItem={props.setSelectedItem}
            isOpenDialog={isOpenDialog}
            unitType={props.unitType}
          />
        ) : (
          <></>
        )}
      </Stack>
    </Container>
  );
};
/**
 * 表示しているグループを切り替え（現在表示しているグループのgroupListでの位置-1）
 * @param groupID 現在表示しているグループのID
 * @param setDisplayGroup 表示しているグループの情報のset関数
 * @param groupList 表示できるグループ一覧
 */
const switchUpGroup = (
  groupID: string,
  setDisplayGroup: React.Dispatch<React.SetStateAction<GroupInfoType | null>>,
  groupList: GroupInfoType[]
) => {
  //表示しているグループを検索
  const currentGroupIndex = groupList.findIndex(groupInfo => {
    return groupInfo.groupID === groupID;
  });

  if (currentGroupIndex === -1 || currentGroupIndex + 1 === groupList.length) {
    setDisplayGroup(groupList[0]);
  } else {
    setDisplayGroup(groupList[currentGroupIndex + 1]);
  }
};
/**
 * 表示しているグループを切り替え（現在表示しているグループのgroupListでの位置+1）
 * @param groupID 現在表示しているグループのID
 * @param setDisplayGroup 表示しているグループの情報のset関数
 * @param groupList 表示できるグループ一覧
 */
const switchDownGroup = (
  groupID: string,
  setDisplayGroup: React.Dispatch<React.SetStateAction<GroupInfoType | null>>,
  groupList: GroupInfoType[]
) => {
  //表示しているグループを検索
  const currentGroupIndex = groupList.findIndex(groupInfo => {
    return groupInfo.groupID === groupID;
  });

  if (currentGroupIndex === -1 || currentGroupIndex - 1 < 0) {
    setDisplayGroup(groupList[groupList.length - 1]);
  } else {
    setDisplayGroup(groupList[currentGroupIndex - 1]);
  }
};
/**
 * シングルビュー
 * @param props props
 * @returns JSX.Element
 */
const SingleView = (props: {
  /** サイトID */
  siteID: string;
  /** グループ一覧 */
  groupList: GroupInfoType[];
  /** 表示するグループの情報 */
  displayGroup: GroupInfoType;
  /** 表示するグループの情報の更新関数 */
  setDisplayGroup: React.Dispatch<React.SetStateAction<GroupInfoType | null>>;
  /** ツールチップの項目数 */
  floorPlan: displaySettingType['singleFloorPlan'];
  /** 選択された室内機 */
  selectedItem: string;
  /** 選択された室内機の更新関数 */
  setSelectedItem: React.Dispatch<React.SetStateAction<string>> | undefined;
  /** ダイアログを表示するかどうか */
  isOpenDialog: boolean;
  /** 機種型式 */
  unitType: 'Hyozan' | 'Modbus';
}) => {
  const trans = useTranslation().t; //翻訳関数
  const groupLayoutElement = useRef<HTMLImageElement>(null); //画像表示用要素

  //prettier-ignore
  //表示している室内機の状態値
  const [displayGroupStatus, setDisplayGroupStatus] = useState<iuStateType[] | null>(null);
  //プルダウン
  const [displayMenu, setDisplayMenu] = useState<null | HTMLElement>(null);
  const displayMenuOpen = Boolean(displayMenu);
  //現在時刻（状態値の定期更新用）
  const [nowTime, setNowTime] = useState(new Date());

  /** 現在表示しているグループの全体での位置 */
  const currentGroupIndex = props.groupList.findIndex(groupInfo => {
    return groupInfo.groupID === props.displayGroup.groupID;
  });

  //レイアウト画像を表示
  useEffect(() => {
    //画像・要素を取得できない場合
    if (
      groupLayoutElement.current === null ||
      props.displayGroup.layout === null
    ) {
      return;
    }

    groupLayoutElement.current.src = props.displayGroup.layout;
  }, [groupLayoutElement, props.displayGroup]);

  /** グループに所属するユニットクーラの状態値を取得する処理 */
  const getGroupStates = () => {
    //Hyozanの場合
    if (props.unitType === 'Hyozan') {
      getStatesSitesRt(props.siteID, {
        'fields':
          'run-stop,preset-temperature,inside-temperature,difference-level,connection-status',
        'group-id': props.displayGroup.groupID,
      }).then(response => {
        if (response['rt-states'].length === 0) {
          return 'NG';
        }
        setDisplayGroupStatus(
          response['rt-states'][0]['indoor-unit-states'].map(iuState => {
            return {
              iuID: iuState['unit-id'],
              runStop:
                iuState['run-stop'] === undefined
                  ? 'stop'
                  : iuState['run-stop'],
              presetTemp:
                iuState['preset-temperature'] === undefined
                  ? ''
                  : iuState['preset-temperature'],
              insideTemp:
                iuState['inside-temperature'] === undefined
                  ? ''
                  : iuState['inside-temperature'],
              differenceLevel:
                iuState['difference-level'] === undefined
                  ? 'safety'
                  : iuState['difference-level'],
              connectionStatus:
                iuState['connection-status'] === undefined
                  ? 'notConnected'
                  : iuState['connection-status'],
            };
          })
        );
      });
    } else if (props.unitType === 'Modbus') {
      //Modbusの場合
      getStatesSitesModbus(props.siteID, {
        'fields':
          'run-stop,preset-temperature,inside-temperature,difference-level',
        'group-id': props.displayGroup.groupID,
      }).then(response => {
        if (response['modbus-states'].length === 0) {
          return 'NG';
        }
        setDisplayGroupStatus(
          response['modbus-states'][0]['indoor-unit-states'].map(iuState => {
            return {
              iuID: iuState['unit-id'],
              runStop:
                iuState['run-stop'] === undefined
                  ? 'stop'
                  : iuState['run-stop'],
              presetTemp:
                iuState['preset-temperature'] === undefined
                  ? ''
                  : iuState['preset-temperature'],
              insideTemp:
                iuState['inside-temperature'] === undefined
                  ? ''
                  : iuState['inside-temperature'],
              differenceLevel:
                iuState['difference-level'] === undefined
                  ? 'safety'
                  : iuState['difference-level'],
            };
          })
        );
      });
    }
  };

  //グループに所属するユニットクーラの状態値を取得
  useEffect(() => {
    getGroupStates();
  }, [props.siteID, props.displayGroup, props.unitType]);

  //タイマ動作後にユニットクーラの状態値を再取得
  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowTime(new Date());
      getGroupStates();
    }, 60000);

    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTime]);

  //表示グループ選択変更時
  const handleClickDisplayGroup = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDisplayMenu(event.currentTarget);
  };
  const handleChangeDisplayGroup = (groupID: string) => {
    const groupInfo = props.groupList.find(info => info.groupID === groupID);
    if (groupInfo !== undefined) {
      props.setDisplayGroup(groupInfo);
      setDisplayMenu(null);
    }
  };

  return (
    <Stack spacing={3}>
      {/* グループレイアウト */}
      <Grid container>
        <Grid item xs>
          <Stack>
            {/* グループセレクトボタン */}
            <Box className="modbusGroupLayoutArea__main__groupSelect">
              <Button
                aria-controls={
                  displayMenuOpen ? 'Single-Display-Group-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={displayMenuOpen ? 'true' : undefined}
                onClick={handleClickDisplayGroup}
                endIcon={
                  <img
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `HCCVQGroupDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }
                className="modbusGroupLayoutArea__main__groupSelect__button">
                <Box
                  title={props.displayGroup.groupName}
                  className="modbusGroupLayoutArea__main__groupSelect__button__text">
                  {props.displayGroup.groupName}
                </Box>
              </Button>
              <Menu
                open={displayMenuOpen}
                anchorEl={displayMenu}
                onClose={() => setDisplayMenu(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                {props.groupList.map(groupInfo => (
                  <MenuItem
                    key={groupInfo.groupID}
                    onClick={() => handleChangeDisplayGroup(groupInfo.groupID)}>
                    {groupInfo.groupName}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            {/* レイアウト画面 */}
            <Box className="modbusGroupLayoutArea__main__layout">
              {props.displayGroup.layout === null ? (
                <Skeleton variant="rectangular">
                  <Box className="modbusGroupLayoutArea__main__layout__box--single"></Box>
                </Skeleton>
              ) : (
                <Box className="modbusGroupLayoutArea__main__layout__box--single">
                  <img
                    ref={groupLayoutElement}
                    alt={`${trans(
                      'HCCVQGroupDetail.image.alternativeText.groupLayoutImage'
                    )}`}
                    className="modbusGroupLayoutArea__main__layout__image"
                  />
                  {displayGroupStatus === null ? (
                    <></>
                  ) : (
                    <UnitIcon
                      iuInfo={props.displayGroup.unitInformations}
                      iuState={displayGroupStatus}
                      floorPlan={props.floorPlan}
                      selectedItem={props.selectedItem}
                      setSelectedItem={props.setSelectedItem}
                      isOpenDialog={props.isOpenDialog}
                      isGroupSelectMenu={displayMenuOpen}
                      groupLayoutElement={groupLayoutElement}
                      displayMode={'single'}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </Grid>
        {/* 現在のグループ位置（表示するグループが10個以下のみ） */}
        <Grid
          item
          xs="auto"
          className="modbusGroupLayoutArea__navigation__circle">
          <Stack>
            {props.groupList.length <= 10 &&
              props.groupList.map(groupInfo => {
                return (
                  <IconButton
                    size="small"
                    key={`${groupInfo.groupID}Circle`}
                    onClick={() => props.setDisplayGroup(groupInfo)}>
                    <CircleIcon
                      fontSize="small"
                      className={
                        groupInfo.groupID === props.displayGroup.groupID
                          ? 'modbusGroupLayoutArea__navigation__circle--current'
                          : 'modbusGroupLayoutArea__navigation__circle--other'
                      }
                    />
                  </IconButton>
                );
              })}
          </Stack>
        </Grid>
      </Grid>
      {/* 操作ボタン */}
      <Box>
        <Stack>
          <Box className="modbusGroupLayoutArea__navigation__arrow">
            <IconButton
              onClick={() =>
                switchDownGroup(
                  props.displayGroup.groupID,
                  props.setDisplayGroup,
                  props.groupList
                )
              }
              disabled={currentGroupIndex === 0}>
              <Box
                className={`modbusGroupLayoutArea__navigation__arrow__icon__border ${
                  currentGroupIndex === 0
                    ? 'modbusGroupLayoutArea__navigation__arrow__icon--disabled'
                    : ''
                }`}>
                <img
                  src={iconBlack.ARROW_UP.src}
                  alt={`${trans(
                    `HCCVQGroupDetail.image.alternativeText.${iconBlack.ARROW_UP.alt}`
                  )}`}
                  className="modbusGroupLayoutArea__navigation__arrow__icon__border__image"
                />
              </Box>
            </IconButton>
          </Box>
          <Box className="modbusGroupLayoutArea__navigation__arrow">
            <IconButton
              onClick={() =>
                switchUpGroup(
                  props.displayGroup.groupID,
                  props.setDisplayGroup,
                  props.groupList
                )
              }
              disabled={currentGroupIndex === props.groupList.length - 1}>
              <Box
                className={`modbusGroupLayoutArea__navigation__arrow__icon__border ${
                  currentGroupIndex === props.groupList.length - 1
                    ? 'modbusGroupLayoutArea__navigation__arrow__icon--disabled'
                    : ''
                }`}>
                <img
                  src={iconBlack.ARROW_DOWN.src}
                  alt={`${trans(
                    `HCCVQGroupDetail.image.alternativeText.${iconBlack.ARROW_DOWN.alt}`
                  )}`}
                  className="modbusGroupLayoutArea__navigation__arrow__icon__border__image"
                />
              </Box>
            </IconButton>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};
/**
 * マルチビュー
 * @param props props
 * @returns JSX.Element
 */
const MultiView = (props: {
  /** サイトID */
  siteID: string;
  /** グループ一覧 */
  displayGroupList: GroupInfoType[];
  /** 表示するグループの情報 */
  displayGroup: GroupInfoType[];
  /** 表示するグループの情報の更新関数 */
  setDisplayGroup: React.Dispatch<React.SetStateAction<GroupInfoType[]>>;
  /** ツールチップのサイズ */
  floorPlan: displaySettingType['multiFloorPlan'];
  /** 選択された室内機 */
  selectedItem: string;
  /** 選択された室内機の更新関数 */
  setSelectedItem: React.Dispatch<React.SetStateAction<string>> | undefined;
  /** ダイアログを表示するかどうか */
  isOpenDialog: boolean;
  /** 機種型式 */
  unitType: 'Hyozan' | 'Modbus';
}) => {
  const trans = useTranslation().t; //翻訳関数
  //画像表示用要素
  //要素数が2以下の場合：[0]：左、[1]：右   要素数が3以上の場合：[0]：左上、[1]：右上、[2]：左下、[3]：右下
  const groupLayoutElement = useRef<React.RefObject<HTMLImageElement>[]>([]);
  [...Array(4)].forEach((_, index) => {
    groupLayoutElement.current[index] = createRef<HTMLImageElement>();
  });

  //prettier-ignore
  //表示している室内機の状態値
  const [displayGroupStatus, setDisplayGroupStatus] = useState<{[groupID: string]: iuStateType[]}>({});
  //プルダウン
  //要素数が2以下の場合：[0]：左、[1]：右
  //要素数が3以上の場合：[0]：左上、[1]：右上、[2]：左下、[3]：右下
  const [displayMenu, setDisplayMenu] = useState<(null | HTMLElement)[]>([
    null,
    null,
    null,
    null,
  ]);
  //現在時刻（状態値の定期更新用）
  const [nowTime, setNowTime] = useState(new Date());
  const displayOpen = displayMenu.map(data => Boolean(data));

  //レイアウト画像を表示
  useEffect(() => {
    groupLayoutElement.current = groupLayoutElement.current.map(
      (element, index) => {
        if (index >= props.displayGroup.length) {
          return element;
        }
        const layout = props.displayGroup[index].layout;
        if (layout === null) {
          return element;
        }

        let newElement = element;
        if (newElement.current !== null) {
          newElement.current.src = layout;
        }
        return newElement;
      }
    );
  }, [groupLayoutElement, props.displayGroup]);

  /** グループに所属するユニットクーラの状態値を取得する処理 */
  const getGroupListStates = () => {
    if (props.unitType === 'Hyozan') {
      getStatesSitesRt(props.siteID, {
        'fields':
          'run-stop,preset-temperature,inside-temperature,difference-level,connection-status',
        'group-id': props.displayGroupList.map(info => info.groupID).toString(),
      }).then(response => {
        if (response['rt-states'].length === 0) {
          return 'NG';
        }
        const groupStates = props.displayGroupList.reduce(
          (result, groupInfo) => {
            result[groupInfo.groupID] = response['rt-states'][0][
              'indoor-unit-states'
            ]
              //グループに登録されている空調機のみ抽出
              .filter(iuState => {
                return groupInfo.unitInformations.some(
                  iuInfo => iuInfo.unitID === iuState['unit-id']
                );
              })
              .map(iuState => {
                return {
                  connectionStatus: iuState['connection-status'],
                  iuID: iuState['unit-id'],
                  runStop:
                    iuState['run-stop'] === undefined
                      ? 'stop'
                      : iuState['run-stop'],
                  presetTemp:
                    iuState['preset-temperature'] === undefined
                      ? ''
                      : iuState['preset-temperature'],
                  insideTemp:
                    iuState['inside-temperature'] === undefined
                      ? ''
                      : iuState['inside-temperature'],
                  differenceLevel:
                    iuState['difference-level'] === undefined
                      ? 'safety'
                      : iuState['difference-level'],
                };
              });
            return result;
          },
          {} as typeof displayGroupStatus
        );
        setDisplayGroupStatus(groupStates);
      });
    } else if (props.unitType === 'Modbus') {
      getStatesSitesModbus(props.siteID, {
        'fields':
          'run-stop,preset-temperature,inside-temperature,difference-level,connection-status',
        'group-id': props.displayGroupList.map(info => info.groupID).toString(),
      }).then(response => {
        if (response['modbus-states'].length === 0) {
          return 'NG';
        }
        const groupStates = props.displayGroupList.reduce(
          (result, groupInfo) => {
            result[groupInfo.groupID] = response['modbus-states'][0][
              'indoor-unit-states'
            ]
              //グループに登録されている空調機のみ抽出
              .filter(iuState => {
                return groupInfo.unitInformations.some(
                  iuInfo => iuInfo.unitID === iuState['unit-id']
                );
              })
              .map(iuState => {
                return {
                  iuID: iuState['unit-id'],
                  runStop:
                    iuState['run-stop'] === undefined
                      ? 'stop'
                      : iuState['run-stop'],
                  presetTemp:
                    iuState['preset-temperature'] === undefined
                      ? ''
                      : iuState['preset-temperature'],
                  insideTemp:
                    iuState['inside-temperature'] === undefined
                      ? ''
                      : iuState['inside-temperature'],
                  differenceLevel:
                    iuState['difference-level'] === undefined
                      ? 'safety'
                      : iuState['difference-level'],
                };
              });
            return result;
          },
          {} as typeof displayGroupStatus
        );
        setDisplayGroupStatus(groupStates);
      });
    }
  };

  //グループに所属するユニットクーラの状態値を取得
  useEffect(() => {
    getGroupListStates();
  }, [props.siteID, props.displayGroupList, props.unitType]);

  //タイマ動作後にユニットクーラの状態値を再取得
  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowTime(new Date());
      getGroupListStates();
    }, 60000);

    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTime]);

  /**
   * 枠線表示用クラス名の決定
   * @param groupIndex 配列内での位置
   * @returns クラス名
   */
  const borderClassName = (groupIndex: number) => {
    switch (groupIndex) {
      case 0:
        return 'modbusGroupLayoutArea__main--1stGroup';
      case 1:
        return 'modbusGroupLayoutArea__main--2ndGroup';
      case 2:
        return 'modbusGroupLayoutArea__main--3rdGroup';
      case 3:
        return 'modbusGroupLayoutArea__main--4thGroup';
      default:
        return '';
    }
  };

  /**
   * 表示グループ選択変更時
   * @param group
   * @param index
   */
  const handleChangeDisplayGroup = (group: GroupInfoType, index: number) => {
    let newDisplayGroup = JSON.parse(JSON.stringify(props.displayGroup));
    newDisplayGroup[index] = group;
    props.setDisplayGroup(newDisplayGroup);

    setDisplayMenu(
      displayMenu.map((data, dataIndex) => {
        if (dataIndex === index) {
          return null;
        }
        return data;
      })
    );
  };

  return (
    <Grid container spacing={5}>
      {props.displayGroup.map((groupInfo, index) => (
        <Grid
          item
          xs={12}
          lg={6}
          key={`menu${index}${groupInfo.groupID}`}
          className={borderClassName(index)}>
          <Stack>
            {/* グループセレクトボタン */}
            <Box className="modbusGroupLayoutArea__main__groupSelect">
              <Button
                aria-controls={
                  displayOpen[index] ? 'Mingle-Display-Group-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={displayOpen[index] ? 'true' : undefined}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  setDisplayMenu(
                    displayMenu.map((data, dataIndex) => {
                      if (dataIndex === index) {
                        return event.currentTarget;
                      }
                      return null;
                    })
                  );
                }}
                endIcon={
                  <img
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `HCCVQGroupDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }
                className="modbusGroupLayoutArea__main__groupSelect__button">
                <Box
                  title={groupInfo.groupName}
                  className="modbusGroupLayoutArea__main__groupSelect__button__text">
                  {groupInfo.groupName}
                </Box>
              </Button>
              <Menu
                open={displayOpen[index]}
                anchorEl={displayMenu[index]}
                onClose={() => {
                  setDisplayMenu(
                    displayMenu.map((data, dataIndex) => {
                      if (dataIndex === index) {
                        return null;
                      }
                      return data;
                    })
                  );
                }}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                {props.displayGroupList.map((selectInfo, selectIndex) => (
                  <MenuItem
                    key={`menuItem${selectIndex}${selectInfo.groupID}`}
                    onClick={() => handleChangeDisplayGroup(selectInfo, index)}>
                    {selectInfo.groupName}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            {/* レイアウト画面 */}
            <Box className="modbusGroupLayoutArea__main__layout">
              {groupInfo.layout === null ? (
                <Skeleton variant="rectangular">
                  <Box className="modbusGroupLayoutArea__main__layout__box--multi"></Box>
                </Skeleton>
              ) : (
                <Box className="modbusGroupLayoutArea__main__layout__box--multi">
                  <img
                    ref={groupLayoutElement.current[index]}
                    alt={`${trans(
                      'HCCVQGroupDetail.image.alternativeText.groupLayoutImage'
                    )}`}
                    className="modbusGroupLayoutArea__main__layout__image"
                  />
                  {groupInfo.groupID in displayGroupStatus === false ||
                  displayGroupStatus[groupInfo.groupID].length === 0 ? (
                    <></>
                  ) : (
                    <UnitIcon
                      iuInfo={groupInfo.unitInformations}
                      iuState={displayGroupStatus[groupInfo.groupID]}
                      floorPlan={props.floorPlan}
                      selectedItem={props.selectedItem}
                      setSelectedItem={props.setSelectedItem}
                      isOpenDialog={props.isOpenDialog}
                      isGroupSelectMenu={displayOpen[index]}
                      groupLayoutElement={groupLayoutElement.current[index]}
                      displayMode="multi"
                    />
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
};
/**
 * 空調機アイコン
 * @param props props
 * @returns JSX
 */
const UnitIcon = (props: {
  /** 表示する室内機の情報 */
  iuInfo: GroupInfoType['unitInformations'];
  /** 表示する室内機の状態値 */
  iuState: iuStateType[];
  /** ツールチップの項目数 */
  floorPlan: '1' | '2' | '3';
  /** 表示する室内機 */
  selectedItem: string;
  /** 表示する室内機の更新関数 */
  setSelectedItem: React.Dispatch<React.SetStateAction<string>> | undefined;
  /** ダイアログを表示するかどうか */
  isOpenDialog: boolean;
  /** セレクトメニューを表示するかどうか */
  isGroupSelectMenu: boolean;
  groupLayoutElement: React.RefObject<HTMLImageElement>;
  displayMode: 'single' | 'multi';
}) => {
  const trans = useTranslation().t; //翻訳関数

  // ツールチップの状態を初期化および更新するための関数
  const initializeTooltipOpen = (
    info: typeof props.iuInfo
  ): Record<string, boolean> => {
    return info.reduce<Record<string, boolean>>((result, item) => {
      result[item.unitID] = true;
      return result;
    }, {});
  };

  //ツールチップの開閉
  const [tooltipOpen, setTooltipOpen] = useState(() =>
    initializeTooltipOpen(props.iuInfo)
  );

  useEffect(() => {
    setTooltipOpen(initializeTooltipOpen(props.iuInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.iuInfo]);

  const handleTooltipClick = (iuID: string) => {
    setTooltipOpen({...tooltipOpen, ...{[iuID]: !tooltipOpen[iuID]}});
  };

  /**
   * アイコンの表示する横位置を算出
   * @param param param
   * @returns アイコンの表示する左上からの位置(割合)
   */
  const iconPosX = (props: {
    /** X座標（相対座標） */
    unitPosX: number;
    /** レイアウトの横幅 */
    width: number;
    /** レイアウトの縦幅 */
    height: number;
    /** 登録したレイアウト画像ファイルの横幅 */
    imgWidth: number;
    /** 登録したレイアウト画像ファイルの縦幅 */
    imgHeight: number;
    /** 表示モード */
    displayMode: 'single' | 'multi';
  }) => {
    const widthMinRatio = props.imgWidth / 280;
    const heightMinRatio = props.imgHeight / 200;
    const maxWidth = 700;
    const maxHeight = 500;
    const minWidth = 280;
    const minHeight = 200;

    const unitPosX = props.unitPosX;
    const width = props.width;
    const height = props.height;
    const imgWidth = props.imgWidth;
    const imgHeight = props.imgHeight;
    const displayMode = props.displayMode;

    // レイアウト設定の横のpixel数で比率割掛け;
    //  シングルビュー
    if (displayMode === 'single') {
      // 登録レイアウト画像が最小描画サイズより縦横共に小さい
      if (imgWidth < minWidth && imgHeight < minHeight) {
        // 最小描画サイズに対して縦の比率が大きい
        if (widthMinRatio < heightMinRatio) {
          return (maxWidth / width) * unitPosX;
        } else {
          return (maxWidth * unitPosX * imgHeight) / (imgWidth * minHeight);
        }
        // 登録レイアウト画像の縦幅が最小描画サイズより小さい
      } else if (imgWidth >= minWidth && imgHeight < minHeight) {
        return unitPosX;
        // 登録レイアウト画像の横幅が最小描画サイズより小さい
      } else if (imgWidth < minWidth && imgHeight >= minHeight) {
        return (maxWidth / width) * unitPosX;
        //登録レイアウト画像が縦横共に大きい
      } else if (imgWidth >= minWidth && imgHeight >= minHeight) {
        return (maxWidth / width) * unitPosX;
      }
      // マルチビュー
    } else if (displayMode === 'multi') {
      // 登録レイアウト画像が最小描画サイズより縦横共に小さい
      if (imgWidth < minWidth && imgHeight < minHeight) {
        // 最小描画サイズに対して縦の比率が大きい
        if (widthMinRatio < heightMinRatio) {
          return (imgWidth / imgHeight) * ((maxWidth * unitPosX) / minWidth);
        } else {
          return (maxWidth * unitPosX) / ((imgWidth * minHeight) / imgHeight);
        }
        // 登録レイアウト画像の縦幅が最小描画サイズより小さい
      } else if (imgWidth >= minWidth && imgHeight < minHeight) {
        return unitPosX;
        // 登録レイアウト画像の横幅が最小描画サイズより小さい
      } else if (imgWidth < minWidth && imgHeight >= minHeight) {
        // レイアウト設定で横幅の最小サイズにフィットされる場合
        if ((minWidth / imgWidth) * imgHeight > maxHeight) {
          return (height * ((maxWidth * unitPosX) / maxHeight)) / width;
        }
        return (
          ((height / imgHeight) * imgWidth * (maxWidth * unitPosX)) /
          minWidth /
          width
        );
        //登録レイアウト画像が縦横共に大きい
      } else if (imgWidth >= minWidth && imgHeight >= minHeight) {
        if (imgHeight > maxHeight) {
          return ((height / maxHeight) * (maxWidth * unitPosX)) / width;
        } else {
          if (widthMinRatio < heightMinRatio) {
            return ((height / imgHeight) * (maxWidth * unitPosX)) / width;
          } else {
            return unitPosX;
          }
        }
      }
    } else {
      return unitPosX;
    }
  };
  /**
   * アイコンの表示する縦位置を算出
   * @param param param
   * @returns アイコンの表示する左上からの位置(割合)
   */
  const iconPosY = (props: {
    /** Y座標（相対座標） */
    unitPosY: number;
    /** レイアウトの横幅 */
    width: number;
    /** レイアウトの縦幅 */
    height: number;
    /** 登録したレイアウト画像ファイルの横幅 */
    imgWidth: number;
    /** 登録したレイアウト画像ファイルの縦幅 */
    imgHeight: number;
    /** 表示モード */
    displayMode: 'single' | 'multi';
  }) => {
    const widthMinRatio = props.imgWidth / 280;
    const heightMinRatio = props.imgHeight / 200;
    const maxWidth = 700;
    const maxHeight = 500;
    const minWidth = 280;
    const minHeight = 200;

    const unitPosY = props.unitPosY;
    const width = props.width;
    const height = props.height;
    const imgWidth = props.imgWidth;
    const imgHeight = props.imgHeight;
    const displayMode = props.displayMode;

    // レイアウト設定の横のpixel数で比率割掛け
    //  シングルビュー
    if (displayMode === 'single') {
      // 登録レイアウト画像が最小描画サイズより縦横共に小さい
      if (imgWidth < minWidth && imgHeight < minHeight) {
        // 最小描画サイズに対して縦の比率が大きい
        if (widthMinRatio < heightMinRatio) {
          return (maxHeight / height) * unitPosY;
        } else {
          return (
            ((width / imgWidth) *
              imgHeight *
              ((maxHeight * unitPosY) / minHeight)) /
            height
          );
        }
        // 登録レイアウト画像の縦幅が最小描画サイズより小さい
      } else if (imgWidth >= minWidth && imgHeight < minHeight) {
        return (width / maxWidth) * (height / imgHeight) * unitPosY;
        // 登録レイアウト画像の横幅が最小描画サイズより小さい
      } else if (imgWidth < minWidth && imgHeight >= minHeight) {
        return (width * (maxHeight * unitPosY)) / minWidth / height;
        //登録レイアウト画像が縦横共に大きい
      } else if (imgWidth >= minWidth && imgHeight >= minHeight) {
        return (maxHeight / height) * unitPosY;
      }
      // マルチビュー
    } else if (displayMode === 'multi') {
      // 登録レイアウト画像が最小描画サイズより縦横共に小さい
      if (imgWidth < minWidth && imgHeight < minHeight) {
        // 最小描画サイズに対して縦の比率が大きい
        if (widthMinRatio < heightMinRatio) {
          return (maxHeight * unitPosY) / ((imgHeight * minWidth) / imgWidth);
        } else {
          return (maxHeight / minHeight) * (imgHeight / height) * unitPosY;
        }
        // 登録レイアウト画像の縦幅が最小描画サイズより小さい
      } else if (imgWidth >= minWidth && imgHeight < minHeight) {
        return (((maxHeight * unitPosY) / maxWidth) * width) / height;
        // 登録レイアウト画像の横幅が最小描画サイズより小さい
      } else if (imgWidth < minWidth && imgHeight >= minHeight) {
        // 登録レイアウト画像の縦幅が最大描画サイズより大きい
        if (imgHeight > maxHeight) {
          return unitPosY;
        }
        // レイアウト設定で横幅の最小サイズにフィットされる場合
        if ((minWidth / imgWidth) * imgHeight > maxHeight) {
          return unitPosY;
        }
        return (maxHeight * unitPosY) / ((minWidth / imgWidth) * imgHeight);
        //登録レイアウト画像が縦横共に大きい
      } else if (imgWidth >= minWidth && imgHeight >= minHeight) {
        if (imgHeight > maxHeight) {
          return unitPosY;
        }
        if (widthMinRatio < heightMinRatio) {
          return (maxHeight * unitPosY) / imgHeight;
        } else {
          return ((maxHeight * unitPosY) / maxWidth) * (width / height);
        }
      }
    } else {
      return unitPosY;
    }
  };

  return (
    <>
      {props.iuInfo
        //初期位置のアイコンはスキップ
        .filter(unitInfo => {
          return (
            Number(unitInfo.xCoordinate) >= 0 ||
            Number(unitInfo.yCoordinate) >= 0
          );
        })
        .map(unitInfo => {
          let className =
            'modbusGroupLayoutArea__main__layout__box__iconBox__icon__border--stop';
          let iconSrc = iconBlack.FREEZER_OFF.src;
          let iconAlt = trans(
            `HCCVQGroupDetail.image.alternativeText.${iconBlack.FREEZER_OFF.alt}`
          );

          const state = props.iuState.find(data => {
            return data.iuID === unitInfo.unitID;
          });
          if (state !== undefined) {
            if (state.runStop === 'run') {
              className =
                'modbusGroupLayoutArea__main__layout__box__iconBox__icon__border--run';
              iconSrc = iconWhite.FREEZER_ON.src;
              iconAlt = trans(
                `HCCVQGroupDetail.image.alternativeText.${iconWhite.FREEZER_ON.alt}`
              );
            } else if (
              state.connectionStatus !== 'connected' ||
              state.insideTemp === '' ||
              state.presetTemp === ''
            ) {
              iconSrc = iconBlack.MISSING.src;
              iconAlt = trans(
                `HCCVQGroupDetail.image.alternativeText.${iconBlack.MISSING.alt}`
              );
            }
          }

          return (
            <Box
              key={unitInfo.unitID}
              className="modbusGroupLayoutArea__main__layout__box__iconBox"
              style={{
                top: `${String(
                  iconPosY({
                    unitPosY: Number(unitInfo.yCoordinate),
                    width: Number(
                      props.groupLayoutElement?.current?.offsetWidth
                    ),
                    height: Number(
                      props.groupLayoutElement?.current?.offsetHeight
                    ),
                    imgWidth: Number(
                      props.groupLayoutElement?.current?.naturalWidth
                    ),
                    imgHeight: Number(
                      props.groupLayoutElement?.current?.naturalHeight
                    ),
                    displayMode: props.displayMode,
                  })
                )}%`,
                left: `${String(
                  iconPosX({
                    unitPosX: Number(unitInfo.xCoordinate),
                    width: Number(
                      props.groupLayoutElement?.current?.offsetWidth
                    ),
                    height: Number(
                      props.groupLayoutElement?.current?.offsetHeight
                    ),
                    imgWidth: Number(
                      props.groupLayoutElement?.current?.naturalWidth
                    ),
                    imgHeight: Number(
                      props.groupLayoutElement?.current?.naturalHeight
                    ),
                    displayMode: props.displayMode,
                  })
                )}%`,
              }}>
              <Grid container>
                {/* ツールチップ */}
                <Grid item xs="auto">
                  {state !== undefined && (
                    <StyledEngineProvider injectFirst>
                      <Tooltip
                        arrow={true}
                        placement="top"
                        title={
                          <Box className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip">
                            <TooltipTitle
                              iuState={state}
                              floorPlan={props.floorPlan}
                            />
                          </Box>
                        }
                        open={
                          props.isOpenDialog === false &&
                          props.isGroupSelectMenu === false &&
                          tooltipOpen[unitInfo.unitID]
                        }
                        disableFocusListener={true}
                        disableHoverListener={true}
                        disableTouchListener={true}
                        PopperProps={{
                          className:
                            'modbusGroupLayoutArea__main__layout__box__iconBox__tooltipPopperProps',
                        }}
                        classes={{
                          tooltip:
                            'modbusGroupLayoutArea__main__layout__box__iconBox__tooltip',
                        }}>
                        {/* ユニットアイコン */}
                        <IconButton
                          size="small"
                          onClick={() => {
                            handleTooltipClick(unitInfo.unitID);
                            if (props.setSelectedItem !== undefined) {
                              props.setSelectedItem(unitInfo.unitID);
                            }
                          }}>
                          <Box
                            className={`${className} ${
                              unitInfo.unitID === props.selectedItem
                                ? 'modbusGroupLayoutArea__main__layout__box__iconBox__icon--highlight'
                                : ''
                            }`}>
                            <img
                              src={iconSrc}
                              alt={iconAlt}
                              className="modbusGroupLayoutArea__main__layout__box__iconBox__icon__border__image"
                            />
                          </Box>
                        </IconButton>
                      </Tooltip>
                    </StyledEngineProvider>
                  )}
                </Grid>
                {/* ユニット名称 */}
                <Grid item xs={12}>
                  <Box
                    title={unitInfo.unitName}
                    className="modbusGroupLayoutArea__main__layout__box__iconBox__unitNameBox">
                    <Box
                      component="span"
                      className="modbusGroupLayoutArea__main__layout__box__iconBox__unitNameBox__text">
                      {unitInfo.unitName}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })}
    </>
  );
};
/**
 * ツールチップ
 * @param props props
 * @returns JSX
 */
const TooltipTitle = (props: {
  /** 室内機の状態値 */
  iuState: iuStateType;
  /** ツールチップの項目数 */
  floorPlan: '1' | '2' | '3';
}) => {
  const trans = useTranslation().t; //翻訳関数

  //温度差色変化で切り替える画像、クラス名の決定
  let differenceLevelImage = iconBlack.ACTUAL_TEMPERATURE.src;
  let differenceLevelClassName = '';

  if (
    props.iuState.connectionStatus !== 'connected' ||
    props.iuState.insideTemp === '' ||
    props.iuState.presetTemp === ''
  ) {
    differenceLevelImage = iconWhite.ACTUAL_TEMPERATURE.src;
    differenceLevelClassName =
      'modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text--disconnected';
  } else {
    switch (props.iuState.differenceLevel) {
      case 'safety':
        differenceLevelImage = iconWhite.ACTUAL_TEMPERATURE.src;
        differenceLevelClassName =
          'modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text--safety';
        break;
      case 'carefully(low)':
        differenceLevelImage = iconWhite.ACTUAL_TEMPERATURE.src;
        differenceLevelClassName =
          'modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text--carefullyLow';
        break;
      case 'carefully(high)':
        differenceLevelImage = iconWhite.ACTUAL_TEMPERATURE.src;
        differenceLevelClassName =
          'modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text--carefullyHigh';
        break;
      case 'warning':
        differenceLevelImage = iconWhite.ACTUAL_TEMPERATURE.src;
        differenceLevelClassName =
          'modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text--warning';
        break;
    }
  }

  /**
   * 温度差を算出
   * @param insideTemp 庫内温度
   * @param presetTemp 設定温度
   * @param connectionStatus 接続状態　Modbusの場合デフォルト値として'connected'を設定
   * @returns 温度差
   */
  const differenceTemp = (
    insideTemp: iuStateType['insideTemp'],
    presetTemp: iuStateType['presetTemp'],
    connectionStatus: iuStateType['connectionStatus'] = 'connected'
  ) => {
    //通信異常あるいは状態値なしの場合
    if (
      connectionStatus !== 'connected' ||
      insideTemp === '' ||
      presetTemp === ''
    ) {
      return '---';
    }

    const difference = Number(insideTemp) - Number(presetTemp);
    const convertDifference = invertComma(roundOff(String(difference), 1));

    if (difference >= 0) {
      return `+${convertDifference}`;
    } else {
      return convertDifference;
    }
  };

  /**
   * 表示する温度を生成
   * @param temp 温度
   * @param connectionStatus 接続状態　Modbusの場合デフォルト値として'connected'を設定
   * @returns 表示温度
   */
  const displayTemperature = (
    temp: string,
    connectionStatus: iuStateType['connectionStatus'] = 'connected' // Modbusの場合デフォルト値として'connected'を設定
  ) => {
    //通信異常あるいは状態値なしの場合
    if (connectionStatus !== 'connected' || temp === '') {
      return '---';
    }

    const roundedTemp = roundOff(temp, 1);
    const tempWithInvertedComma = invertComma(roundedTemp);
    return tempWithInvertedComma;
  };

  switch (props.floorPlan) {
    case '1':
      return (
        <Grid
          container
          alignItems="center"
          className={`modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item ${differenceLevelClassName}`}>
          <Grid item xs="auto">
            <img
              src={differenceLevelImage}
              alt={`${trans(
                'HCCVQGroupDetail.image.alternativeText.tempActual'
              )}`}
              className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__icon"
            />
          </Grid>
          <Grid
            item
            xs
            className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__temperature">
            {displayTemperature(
              props.iuState.insideTemp,
              props.iuState.connectionStatus
            )}
            {props.iuState.connectionStatus !== 'connected' ||
            props.iuState.insideTemp === '' ||
            props.iuState.presetTemp === '' ? (
              <Box
                component="span"
                className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__unitBlackTxt">
                {trans('HCCVQGroupDetail.unit.temperatureUnit')}
              </Box>
            ) : (
              <Box
                component="span"
                className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__unit">
                {trans('HCCVQGroupDetail.unit.temperatureUnit')}
              </Box>
            )}
          </Grid>
        </Grid>
      );
    case '2':
      return (
        <Stack>
          {/** 庫内温度 */}
          <Box>
            <Grid
              container
              alignItems="center"
              className={`modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item ${differenceLevelClassName}`}>
              <Grid item xs="auto">
                <img
                  src={differenceLevelImage}
                  alt={`${trans(
                    'HCCVQGroupDetail.image.alternativeText.tempActual'
                  )}`}
                  className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__icon"
                />
              </Grid>
              <Grid
                item
                xs
                className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__temperature">
                {displayTemperature(
                  props.iuState.insideTemp,
                  props.iuState.connectionStatus
                )}
                {props.iuState.connectionStatus !== 'connected' ||
                props.iuState.insideTemp === '' ||
                props.iuState.presetTemp === '' ? (
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__unitBlackTxt">
                    {trans('HCCVQGroupDetail.unit.temperatureUnit')}
                  </Box>
                ) : (
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__unit">
                    {trans('HCCVQGroupDetail.unit.temperatureUnit')}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
          {/** 設定温度 */}
          <Box>
            <Grid
              container
              alignItems="center"
              className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item">
              <Grid item xs="auto">
                <img
                  src={iconBlack.SET_TEMPERATURE.src}
                  alt={`${trans(
                    `HCCVQGroupDetail.image.alternativeText.${iconBlack.SET_TEMPERATURE.alt}`
                  )}`}
                  className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__icon"
                />
              </Grid>
              <Grid
                item
                xs
                className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__temperature">
                {`${displayTemperature(
                  props.iuState.presetTemp,
                  props.iuState.connectionStatus
                )}${trans('HCCVQGroupDetail.unit.temperatureUnit')}`}
              </Grid>
            </Grid>
          </Box>
        </Stack>
      );
    case '3':
      return (
        <Stack spacing={0.5}>
          {/** 庫内温度 */}
          <Box>
            <Grid
              container
              alignItems="center"
              className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item">
              <Grid item xs="auto">
                <img
                  src={iconBlack.ACTUAL_TEMPERATURE.src}
                  alt={`${trans(
                    `HCCVQGroupDetail.image.alternativeText.${iconBlack.ACTUAL_TEMPERATURE.alt}`
                  )}`}
                  className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__icon"
                />
              </Grid>
              <Grid
                item
                xs
                className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__temperature">
                {`${displayTemperature(
                  props.iuState.insideTemp,
                  props.iuState.connectionStatus
                )}${trans('HCCVQGroupDetail.unit.temperatureUnit')}`}
              </Grid>
            </Grid>
          </Box>
          {/** 設定温度 */}
          <Box>
            <Grid
              container
              alignItems="center"
              className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item">
              <Grid item xs="auto">
                <img
                  src={iconBlack.SET_TEMPERATURE.src}
                  alt={`${trans(
                    `HCCVQGroupDetail.image.alternativeText.${iconBlack.SET_TEMPERATURE.alt}`
                  )}`}
                  className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__icon"
                />
              </Grid>
              <Grid
                item
                xs
                className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__temperature">
                {`${displayTemperature(
                  props.iuState.presetTemp,
                  props.iuState.connectionStatus
                )}${trans('HCCVQGroupDetail.unit.temperatureUnit')}`}
              </Grid>
            </Grid>
          </Box>
          {/** 温度差 */}
          <Box>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              className={`modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item ${differenceLevelClassName}`}>
              <Grid item xs="auto" className={differenceLevelClassName}>
                {differenceTemp(
                  props.iuState.insideTemp,
                  props.iuState.presetTemp,
                  props.iuState.connectionStatus
                )}
                {props.iuState.connectionStatus !== 'connected' ||
                props.iuState.insideTemp === '' ||
                props.iuState.presetTemp === '' ? (
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__unitBlackTxt">
                    {trans('HCCVQGroupDetail.unit.temperatureUnit')}
                  </Box>
                ) : (
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__main__layout__box__iconBox__tooltip__item__text__unit">
                    {trans('HCCVQGroupDetail.unit.temperatureUnit')}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Stack>
      );
    default:
      return <></>;
  }
};
/**
 * 表示設定ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const PlayerSettingDialog = (props: {
  /** 別タブ表示 */
  isPopOut: boolean;
  /** ダイアログを表示するかどうか */
  isOpenDialog: boolean;
  /** ダイアログを表示するかどうか更新関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** サイトID */
  siteID: string;
  /** グループ一覧 */
  groupList: GroupInfoType[];
  /** シングルビュー/マルチビュー */
  displayMode: 'player' | 'multiView';
  /** 設定内容 */
  displaySetting: displaySettingType;
  /** 設定内容の更新関数 */
  setDisplaySetting: React.Dispatch<React.SetStateAction<displaySettingType>>;
  /** マルチビューに表示するグループのリスト */
  multiDisplayGroupList: GroupInfoType[];
  /** マルチビューに表示するグループのリストの更新関数 */
  setMultiDisplayGroupList: React.Dispatch<
    React.SetStateAction<GroupInfoType[]>
  >;
}) => {
  //prettier-ignore
  //レイアウト自動切換え
  const [autoSwitching, setAutoSwitching] = useState(props.displaySetting.autoSwitching);
  //自動切換え周期
  const [timeFrame, setTimeFrame] = useState(props.displaySetting.timeFrame);
  //prettier-ignore
  //シングルビュー ツールチップの項目数
  const [singleFloorPlan, setSingleFloorPlan] = useState(props.displaySetting.singleFloorPlan);
  //prettier-ignore
  //マルチビュー ツールチップの項目数
  const [multiFloorPlan, setMultiFloorPlan] = useState(props.displaySetting.multiFloorPlan);
  //prettier-ignore
  //表示できるグループ
  const [multiDisplayGroupList, setMultiDisplayGroupList] = useState(props.multiDisplayGroupList);

  //自動切換え周期用プルダウン
  const [timeFrameMenu, setTimeFrameMenu] = useState<null | HTMLElement>(null);
  const timeFrameMenuOpen = Boolean(timeFrameMenu);
  //prettier-ignore
  //シングルビュー ツールチップの項目数用プルダウン
  const [singleFloorPlanMenu, setSingleFloorPlanMenu] = useState<null | HTMLElement>(null);
  const singleFloorPlanMenuOpen = Boolean(singleFloorPlanMenu);
  //prettier-ignore
  //マルチビュー ツールチップの項目数用プルダウン
  const [multiFloorPlanMenu, setMultiFloorPlanMenu] = useState<null | HTMLElement>(null);
  const multiFloorPlanMenuOpen = Boolean(multiFloorPlanMenu);
  //APIエラー時のエラーダイアログ表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);
  //API応答待ちフラグ
  const [apiWait, setAPIWait] = useState(false);

  const trans = useTranslation().t; //翻訳関数

  //ツールチップサイズ変換テーブル
  const floorPlanMenuTable = {
    '1': trans('HCCVQGroupDetail.dialogBox.item.value.actualTemp'),
    '2': trans('HCCVQGroupDetail.dialogBox.item.value.actualSetTemp'),
    '3': trans('HCCVQGroupDetail.dialogBox.item.value.actualSetDifference'),
  };

  //レイアウト自動切換え変更時
  const handleChangeAutoSwitching = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAutoSwitching(event.target.checked);
  };
  //自動切換え周期変更時
  const handleClickTimeFrame = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTimeFrameMenu(event.currentTarget);
  };
  const handleChangeTimeFrame = (time: displaySettingType['timeFrame']) => {
    setTimeFrame(time);
    setTimeFrameMenu(null);
  };
  //シングルビュー ツールチップの項目数変更時
  const handleClickSingleFloorPlan = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setSingleFloorPlanMenu(event.currentTarget);
  };
  const handleChangeSingleFloorPlan = (
    plan: displaySettingType['singleFloorPlan']
  ) => {
    setSingleFloorPlan(plan);
    setSingleFloorPlanMenu(null);
  };
  //マルチビュー ツールチップの項目数変更時
  const handleClickMultiFloorPlan = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMultiFloorPlanMenu(event.currentTarget);
  };
  const handleChangeMultiFloorPlan = (
    plan: displaySettingType['multiFloorPlan']
  ) => {
    setMultiFloorPlan(plan);
    setMultiFloorPlanMenu(null);
  };
  //マルチビュー 表示グループする変更時
  const handleChangeMultiDisplayGroupList = (groupID: string) => () => {
    const currentIndex = multiDisplayGroupList.findIndex(
      info => info.groupID === groupID
    );
    const newChecked = [...multiDisplayGroupList];

    if (currentIndex === -1) {
      const newGroup = props.groupList.find(info => info.groupID === groupID);
      if (newGroup !== undefined) {
        newChecked.push(newGroup);
      }
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setMultiDisplayGroupList(newChecked);
  };

  /**
   * OKボタン押下時処理
   */
  const editSetting = () => {
    setAPIWait(true);

    //一回目に実施するのAPI
    const firstPromises: Promise<apiIF.res.resultOnly>[] = [
      Promise.resolve({result: 'OK'}),
    ];
    //二回目に実施するのAPI
    const secondPromises: Promise<apiIF.res.resultOnly>[] = [];

    if (props.isPopOut === false) {
      const editUserData: apiIF.reqParameters.patchSettingUser = {
        'modbus-display': {
          'auto-switching': autoSwitching ? 'active' : 'inactive',
          'switching-time-frame': timeFrame,
          'display-item': {
            'single-floor-plan': singleFloorPlan,
            'multi-floor-plan': multiFloorPlan,
          },
        },
      };
      firstPromises.push(patchSettingUser(editUserData));

      if (props.displayMode === 'multiView') {
        let editGroupData: apiIF.reqParameters.patchCompositionsGroups = {};
        props.groupList.forEach(groupInfo => {
          //未表示設定を先に行わないと、登録超過になってしまう
          if (
            multiDisplayGroupList.some(
              info => info.groupID === groupInfo.groupID
            )
          ) {
            editGroupData['state-display'] = 'active';
            secondPromises.push(
              patchCompositionsGroups(
                props.siteID,
                groupInfo.groupID,
                editGroupData
              )
            );
          } else {
            editGroupData['state-display'] = 'inactive';
            firstPromises.push(
              patchCompositionsGroups(
                props.siteID,
                groupInfo.groupID,
                editGroupData
              )
            );
          }
        });
      }
    }

    //ユーザの情報を修正
    //グループの情報を修正
    Promise.all(firstPromises)
      .then(() => {
        if (secondPromises.length === 0) {
          secondPromises.push(Promise.resolve({result: 'OK'}));
        }
        return Promise.all(secondPromises);
      })
      .then(() => {
        props.setDisplaySetting({
          autoSwitching: autoSwitching,
          timeFrame: timeFrame,
          singleFloorPlan: singleFloorPlan,
          multiFloorPlan: multiFloorPlan,
        });
        props.setMultiDisplayGroupList(multiDisplayGroupList);
        props.setIsOpenDialog(false);
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setAutoSwitching(props.displaySetting.autoSwitching);
    setTimeFrame(props.displaySetting.timeFrame);
    setSingleFloorPlan(props.displaySetting.singleFloorPlan);
    setMultiFloorPlan(props.displaySetting.multiFloorPlan);
    setMultiDisplayGroupList(props.multiDisplayGroupList);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle className="modbusGroupLayoutArea__setting__title">
          <Box className="modbusGroupLayoutArea__setting__title__text">
            {trans('HCCVQGroupDetail.dialogBox.title')}
          </Box>
        </DialogTitle>
        <DialogContent className="modbusGroupLayoutArea__setting">
          <List>
            {/** レイアウト自動切換え */}
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__setting__item__title">
                    {trans(
                      'HCCVQGroupDetail.dialogBox.item.title.autoSwitching'
                    )}
                  </Box>
                }
                secondary={
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__setting__item__autoSwitching">
                    {autoSwitching
                      ? trans(
                          'HCCVQGroupDetail.dialogBox.item.value.autoSwitching.on'
                        )
                      : trans(
                          'HCCVQGroupDetail.dialogBox.item.value.autoSwitching.off'
                        )}
                  </Box>
                }
              />
              <Switch
                checked={autoSwitching}
                disabled={props.displayMode === 'multiView'}
                edge="end"
                onChange={handleChangeAutoSwitching}
              />
            </ListItem>
            {/** 自動切換え周期 */}
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__setting__item__title">
                    {trans(
                      'HCCVQGroupDetail.dialogBox.item.title.switchingTimeframe'
                    )}
                  </Box>
                }
              />
            </ListItem>
            <ListItem disableGutters dense>
              <Button
                aria-controls={
                  timeFrameMenuOpen ? 'Switching-timeframe-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={timeFrameMenuOpen ? 'true' : undefined}
                disabled={autoSwitching === false}
                onClick={handleClickTimeFrame}
                endIcon={
                  <img
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `HCCVQGroupDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }
                size="small">
                {timeFrame}&nbsp;
                {trans('HCCVQGroupDetail.dialogBox.item.value.seconds')}
              </Button>
              <Menu
                open={timeFrameMenuOpen}
                anchorEl={timeFrameMenu}
                onClose={() => setTimeFrameMenu(null)}>
                <MenuItem onClick={() => handleChangeTimeFrame('10')}>
                  {trans('HCCVQGroupDetail.dialogBox.item.value.10seconds')}
                </MenuItem>
                <MenuItem onClick={() => handleChangeTimeFrame('20')}>
                  {trans('HCCVQGroupDetail.dialogBox.item.value.20seconds')}
                </MenuItem>
                <MenuItem onClick={() => handleChangeTimeFrame('30')}>
                  {trans('HCCVQGroupDetail.dialogBox.item.value.30seconds')}
                </MenuItem>
                <MenuItem onClick={() => handleChangeTimeFrame('40')}>
                  {trans('HCCVQGroupDetail.dialogBox.item.value.40seconds')}
                </MenuItem>
                <MenuItem onClick={() => handleChangeTimeFrame('50')}>
                  {trans('HCCVQGroupDetail.dialogBox.item.value.50seconds')}
                </MenuItem>
                <MenuItem onClick={() => handleChangeTimeFrame('60')}>
                  {trans('HCCVQGroupDetail.dialogBox.item.value.60seconds')}
                </MenuItem>
              </Menu>
            </ListItem>
            {/** シングルビュー ツールチップサイズ */}
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__setting__item__title">
                    {trans(
                      'HCCVQGroupDetail.dialogBox.item.title.singleViewTooltip'
                    )}
                  </Box>
                }
              />
            </ListItem>
            <ListItem disableGutters dense>
              <Button
                aria-controls={
                  singleFloorPlanMenuOpen ? 'Single-floorplan-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={singleFloorPlanMenuOpen ? 'true' : undefined}
                disabled={props.displayMode === 'multiView'}
                onClick={handleClickSingleFloorPlan}
                endIcon={
                  <img
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `HCCVQGroupDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }>
                {floorPlanMenuTable[singleFloorPlan]}
              </Button>
              <Menu
                open={singleFloorPlanMenuOpen}
                anchorEl={singleFloorPlanMenu}
                onClose={() => setSingleFloorPlanMenu(null)}>
                {(
                  Object.entries(floorPlanMenuTable) as Array<
                    ['1' | '2' | '3', string]
                  >
                ).map(([key, value]) => (
                  <MenuItem
                    key={key}
                    onClick={() => handleChangeSingleFloorPlan(key)}>
                    {value}
                  </MenuItem>
                ))}
              </Menu>
            </ListItem>
            {/** マルチビュー ツールチップの項目数 */}
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__setting__item__title">
                    {trans(
                      'HCCVQGroupDetail.dialogBox.item.title.multiViewTooltip'
                    )}
                  </Box>
                }
              />
            </ListItem>
            <ListItem disableGutters dense>
              <Button
                aria-controls={
                  multiFloorPlanMenuOpen ? 'Multi-floorplan-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={multiFloorPlanMenuOpen ? 'true' : undefined}
                disabled={props.displayMode === 'player'}
                onClick={handleClickMultiFloorPlan}
                endIcon={
                  <img
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `HCCVQGroupDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }>
                {floorPlanMenuTable[multiFloorPlan]}
              </Button>
              <Menu
                open={multiFloorPlanMenuOpen}
                anchorEl={multiFloorPlanMenu}
                onClose={() => setMultiFloorPlanMenu(null)}>
                {(
                  Object.entries(floorPlanMenuTable) as Array<
                    ['1' | '2' | '3', string]
                  >
                ).map(([key, value]) => (
                  <MenuItem
                    key={key}
                    onClick={() => handleChangeMultiFloorPlan(key)}>
                    {value}
                  </MenuItem>
                ))}
              </Menu>
            </ListItem>
            <Divider />
            {/** マルチビュー 表示グループ選択 */}
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Box
                    component="span"
                    className="modbusGroupLayoutArea__setting__item__title">
                    {trans(
                      'HCCVQGroupDetail.dialogBox.item.title.groupToDisplay'
                    )}
                  </Box>
                }
              />
            </ListItem>
            <List component="div" disablePadding dense>
              {props.groupList.map(groupInfo => {
                const buttonDisabled =
                  props.displayMode === 'player' ||
                  (multiDisplayGroupList.length ===
                    limit.DISPLAY_NUM_MODBUS_GROUP_MAX &&
                    multiDisplayGroupList.every(
                      info => info.groupID !== groupInfo.groupID
                    ));
                return (
                  <ListItem key={groupInfo.groupID} disablePadding>
                    <ListItemButton
                      disabled={buttonDisabled}
                      onClick={handleChangeMultiDisplayGroupList(
                        groupInfo.groupID
                      )}>
                      <ListItemIcon>
                        <Checkbox
                          checked={multiDisplayGroupList.some(
                            info => info.groupID === groupInfo.groupID
                          )}
                          edge="start"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            title={groupInfo.groupName}
                            className="modbusGroupLayoutArea__setting__item__groupDisplay">
                            {groupInfo.groupName}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </List>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('HCCVQGroupDetail.dialogBox.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={editSetting}
            buttonText={trans('HCCVQGroupDetail.dialogBox.buttonTxt.ok')}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};

export {GroupDetailPopOut, GroupDetailPopOutLoader};
export default GroupDetail;
